import React, { useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEdit,
  FaTrashAlt,
  FaTimes,
  FaSyncAlt,
  FaSearch,
} from "react-icons/fa";
import { cachePosMasterData, getCachedPosMasterData } from "../utils/posMasterDataCache";
import { getLoyaltyCustomerList } from "../utils/loyaltyApi";
import { getStoredUser } from "../utils/impersonation";
import { getCustomerManagementPermissions, ROLE_UI_PERMISSIONS_EVENT } from "../utils/roleUiPermissions";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  createOfflineParty,
  deleteOfflineParty,
  listOfflineParties,
  updateOfflineParty,
} from "../offline/partyStore";

const color = {
  brand: "var(--app-primary)",
  brandSoft: "var(--app-primary-soft)",
  text: "#111827",
  muted: "#6B7280",
  line: "#E5E7EB",
  bg: "#F2F5F9",
  white: "#fff",
  success: "#2e7d32",
  danger: "#D32F2F",
  dangerSoft: "#FFF5F5",
  grayBtn: "#6c757d",
};

const AddPartyPage = () => {
  const viewer = useMemo(() => getStoredUser(), []);
  const [, setPermissionVersion] = useState(0);
  const customerPermissions = getCustomerManagementPermissions(viewer);
  const [userId, setUserId] = useState(null);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loyaltyPointsByParty, setLoyaltyPointsByParty] = useState({});

  // form state
  const [partyName, setPartyName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [mobile, setMobile] = useState("");
  const [type, setType] = useState("");

  // filters
  const [search, setSearch] = useState("");

  // modal/edit state
  const [isModalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editType, setEditType] = useState("");
  const companyCacheKey = useMemo(
    () => (userId ? `partyCompanyCacheV1_${userId}` : "partyCompanyCacheV1_guest"),
    [userId]
  );

  useEffect(() => {
    const refresh = () => setPermissionVersion((version) => version + 1);
    window.addEventListener(ROLE_UI_PERMISSIONS_EVENT, refresh);
    return () => window.removeEventListener(ROLE_UI_PERMISSIONS_EVENT, refresh);
  }, []);

  const readCompanyCache = () => {
    try {
      const raw = localStorage.getItem(companyCacheKey);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  };

  const writeCompanyCache = (next) => {
    try {
      localStorage.setItem(companyCacheKey, JSON.stringify(next || {}));
    } catch {
      // ignore
    }
  };

  const pickCompanyValue = (party, cache = {}) =>
    String(
      party?.companyname ??
        party?.company_name ??
        party?.partycompanyname ??
        party?.partycompany ??
        party?.companyName ??
        party?.company ??
        cache?.[String(party?.id || "")] ??
        ""
    ).trim();

  // load userId & fetch parties
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData"))?.data;
    if (data?.id) {
      setUserId(data.id);
      fetchParties(data.id);
    } else {
      toast.error("User data not found");
    }
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    if (isFullOfflineModeEnabled()) {
      setLoyaltyPointsByParty({});
      return undefined;
    }
    let active = true;
    getLoyaltyCustomerList(userId)
      .then((result) => active && setLoyaltyPointsByParty(result.points_by_party || {}))
      .catch(() => active && setLoyaltyPointsByParty({}));
    return () => { active = false; };
  }, [userId]);

  const fetchParties = async (restaurantid, opts = {}) => {
    if (isFullOfflineModeEnabled()) {
      if (opts.refresh) setRefreshing(true);
      else setLoading(true);
      try {
        const records = await listOfflineParties(restaurantid);
        setParties(records);
        return records;
      } catch (error) {
        toast.error(error?.message || "Unable to load offline customers");
        return [];
      } finally {
        if (opts.refresh) setRefreshing(false);
        else setLoading(false);
      }
    }
    const cached = await getCachedPosMasterData(restaurantid, "parties");
    const hasCachedParties = Array.isArray(cached?.records) && cached.records.length > 0;
    if (hasCachedParties) {
      setParties(cached.records);
      if (!opts.refresh) setLoading(false);
    }
    if (opts.refresh) setRefreshing(true);
    else if (!hasCachedParties) setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/list/partylistformaster`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid }),
        }
      );
      const json = await res.json();
      if (res.ok && Array.isArray(json.list)) {
        const cache = readCompanyCache();
        const enriched = (json.list || []).map((party) => ({
          ...party,
          companyname: pickCompanyValue(party, cache),
        }));
        setParties(enriched);

        const nextCache = { ...cache };
        enriched.forEach((party) => {
          const key = String(party?.id || "");
          if (!key) return;
          const company = pickCompanyValue(party, cache);
          if (company) nextCache[key] = company;
        });
        writeCompanyCache(nextCache);
        await cachePosMasterData(restaurantid, "parties", enriched);
        return enriched;
      } else {
        toast.error(json.message || "Failed to load parties");
      }
    } catch (e) {
      if (hasCachedParties) {
        toast.info("Offline mode — showing saved customers.");
        return cached.records;
      }
      toast.error(e.message || "Unable to load customers");
    } finally {
      if (opts.refresh) setRefreshing(false);
      else setLoading(false);
    }
    return [];
  };

  // lightweight validators
  const isValidMobile = (v) => /^\d{10}$/.test(String(v || "").trim());
  const isNonEmpty = (v) => String(v || "").trim().length > 0;
  const normalizeType = (raw) => {
    const val = String(raw || "").trim();
    if (!val) return "";
    const lower = val.toLowerCase();
    if (lower.includes("cust")) return "Customer";
    if (lower.includes("sup")) return "Supplier";
    return val;
  };

  const handleSave = async () => {
    if (!customerPermissions.create) return toast.error("You do not have permission to add customers");
    if (!isNonEmpty(partyName)) return toast.warn("Party name is required");
    if (!isValidMobile(mobile)) return toast.warn("Enter 10-digit mobile");
    if (!isNonEmpty(type)) return toast.warn("Please select type");

    try {
      const partyPayload = {
        restaurantid: userId,
        partyname: partyName.trim(),
        companyname: companyName.trim(),
        company_name: companyName.trim(),
        partycontactpersonmobile: String(mobile).trim(),
        type,
      };
      if (isFullOfflineModeEnabled()) {
        const result = await createOfflineParty(userId, partyPayload);
        setParties(result?.parties || []);
        setPartyName("");
        setCompanyName("");
        setMobile("");
        setType("");
        toast.success(result?.existing ? "Customer already exists" : "Party added offline");
        return;
      }
      const res = await fetch(
        API_ENDPOINTS.CREATE_PARTY,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...partyPayload,
            partycompanyname: companyName.trim(),
            partycompany: companyName.trim(),
            company: companyName.trim(),
          }),
        }
      );
      const json = await res.json();
      if (res.ok) {
        toast.success("Party added");
        const savedCompany = companyName.trim();
        const savedName = partyName.trim().toLowerCase();
        const savedMobile = String(mobile).trim();
        setPartyName("");
        setCompanyName("");
        setMobile("");
        setType("");
        const latest = await fetchParties(userId);
        if (savedCompany) {
          const matched = (latest || []).find(
            (p) =>
              String(p?.partyname || "")
                .trim()
                .toLowerCase() === savedName &&
              String(p?.partycontactpersonmobile || "").trim() === savedMobile
          );
          if (matched?.id) {
            const cache = readCompanyCache();
            cache[String(matched.id)] = savedCompany;
            writeCompanyCache(cache);
            setParties((prev) =>
              prev.map((p) =>
                String(p?.id) === String(matched.id)
                  ? { ...p, companyname: savedCompany }
                  : p
              )
            );
          }
        }
      } else {
        toast.error(json.errors?.partyname?.[0] || json.message || "Error");
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parties;
    return parties.filter(
      (p) =>
        String(p.partyname || "").toLowerCase().includes(q) ||
        pickCompanyValue(p)
          .toLowerCase()
          .includes(q) ||
        String(p.partycontactpersonmobile || "").includes(q) ||
        String(normalizeType(p.type || p.partytype || "") || "")
          .toLowerCase()
          .includes(q)
    );
  }, [parties, search]);

  const stats = useMemo(() => {
    const total = parties.length;
    const customers = parties.filter(
      (p) => normalizeType(p.type || p.partytype) === "Customer"
    ).length;
    const suppliers = parties.filter(
      (p) => normalizeType(p.type || p.partytype) === "Supplier"
    ).length;
    return { total, customers, suppliers };
  }, [parties]);

  const clearForm = () => {
    setPartyName("");
    setCompanyName("");
    setMobile("");
    setType("");
  };

  const quickSetType = (val) => setType((prev) => (prev === val ? "" : val));

  const openEditModal = (p) => {
    if (!customerPermissions.edit && !customerPermissions.delete) {
      toast.error("You do not have permission to manage customers");
      return;
    }
    setEditId(p.id);
    setEditName(p.partyname || "");
    setEditCompanyName(pickCompanyValue(p));
    setEditMobile(p.partycontactpersonmobile || "");
    setEditType(normalizeType(p.type || p.partytype || ""));
    setModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!customerPermissions.edit) return toast.error("You do not have permission to edit customers");
    if (!isNonEmpty(editName)) return toast.warn("Name is required");
    if (!isValidMobile(editMobile)) return toast.warn("Enter 10-digit mobile");
    if (!isNonEmpty(editType)) return toast.warn("Please select type");

    try {
      const partyPayload = {
        restaurantid: userId,
        partyid: editId,
        partyname: editName.trim(),
        companyname: editCompanyName.trim(),
        company_name: editCompanyName.trim(),
        partycontactpersonmobile: String(editMobile).trim(),
        type: editType,
      };
      if (isFullOfflineModeEnabled()) {
        const result = await updateOfflineParty(userId, editId, partyPayload);
        setParties(result?.parties || []);
        setModalOpen(false);
        toast.info("Party updated offline");
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/edit/party`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...partyPayload,
            partycompanyname: editCompanyName.trim(),
            partycompany: editCompanyName.trim(),
            company: editCompanyName.trim(),
          }),
        }
      );
      const json = await res.json();
      if (res.ok) {
        toast.info("Party updated");
        if (editId) {
          const cache = readCompanyCache();
          cache[String(editId)] = String(editCompanyName || "").trim();
          writeCompanyCache(cache);
        }
        setModalOpen(false);
        fetchParties(userId);
      } else {
        toast.error(json.message || "Error updating");
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async () => {
    if (!customerPermissions.delete) return toast.error("You do not have permission to delete customers");
    if (!window.confirm("Delete this party?")) return;
    try {
      if (isFullOfflineModeEnabled()) {
        const result = await deleteOfflineParty(userId, editId);
        setParties(result?.parties || []);
        setModalOpen(false);
        toast.success("Party deleted offline");
        return;
      }
      const res = await fetch(
        `${API_BASE_URL}/delete/party`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, partyid: editId }),
        }
      );
      const json = await res.json();
      if (res.ok) {
        toast.error("Party deleted");
        setModalOpen(false);
        fetchParties(userId);
      } else {
        toast.error(json.message || "Error deleting");
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Wrap>
      <ToastContainer position="top-right" autoClose={2500} />

      <TopBar>
        <TopLeft>
          <TopTitle>Parties</TopTitle>
          <TopSub>Customers / Suppliers — manage quickly like mobile.</TopSub>
        </TopLeft>
        <TopActions>
          <TopPill>Total: {stats.total}</TopPill>
          <TopPill>Customers: {stats.customers}</TopPill>
          <TopPill>Suppliers: {stats.suppliers}</TopPill>
          <ActionBtn
            onClick={() => fetchParties(userId, { refresh: true })}
            disabled={refreshing}
          >
            <FaSyncAlt />
            {refreshing ? "Refreshing..." : "Refresh"}
          </ActionBtn>
        </TopActions>
      </TopBar>

      {/* Add Form Card */}
      {customerPermissions.create ? <Card>
        <CardTitleRow>
          <span>New Party</span>
        </CardTitleRow>
        <FormRow>
          <Input
            placeholder="Party Name"
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
          />
          <Input
            placeholder="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <Input
            placeholder="Mobile Number (10 digits)"
            value={mobile}
            onChange={(e) =>
              setMobile(e.target.value.replace(/[^\d]/g, "").slice(0, 10))
            }
            inputMode="numeric"
          />
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">-- Select Type --</option>
            <option value="Customer">Customer</option>
            <option value="Supplier">Supplier</option>
          </Select>
        </FormRow>
        <HelpRow>
          <HelpLine>
            Tip: Name min 2 chars • Mobile must be 10 digits • Choose correct type
          </HelpLine>
          <QuickTypeRow>
            <QuickChip
              $active={type === "Customer"}
              onClick={() => quickSetType("Customer")}
            >
              Customer
            </QuickChip>
            <QuickChip
              $active={type === "Supplier"}
              onClick={() => quickSetType("Supplier")}
            >
              Supplier
            </QuickChip>
            <QuickChip onClick={clearForm}>Clear</QuickChip>
          </QuickTypeRow>
        </HelpRow>
        <FormActions>
          <PrimaryBtn onClick={handleSave}>
            <span>Save Party</span>
          </PrimaryBtn>
        </FormActions>
      </Card> : <PermissionNotice>You can view customers, but adding new customers is disabled for your role.</PermissionNotice>}

      {/* List Card */}
      <Card>
        <CardTitleRow>
          <span>Party List</span>
          <SearchBox>
            <FaSearch />
            <input
              placeholder="Search name / company / mobile / type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchBox>
        </CardTitleRow>
        {loading ? (
          <Loading>Loading…</Loading>
        ) : filtered.length === 0 ? (
          <Empty>Nothing here yet. Add your first party above.</Empty>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Company</Th>
                  <Th>Mobile</Th>
                  <Th>Type</Th>
                  <Th>Loyalty</Th>
                  <Th style={{ width: 110 }}>Action</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <Td>{p.partyname}</Td>
                    <Td>
                      {pickCompanyValue(p) || "-"}
                    </Td>
                    <Td>{p.partycontactpersonmobile}</Td>
                    <Td>
                      <TypeBadge $type={normalizeType(p.type || p.partytype)}>
                        {normalizeType(p.type || p.partytype) || "-"}
                      </TypeBadge>
                    </Td>
                    <Td>
                      {loyaltyPointsByParty[String(p.id)] !== undefined ? (
                        <PointsBadge>★ {Number(loyaltyPointsByParty[String(p.id)] || 0).toLocaleString()} pts</PointsBadge>
                      ) : "—"}
                    </Td>
                    <Td>
                      {customerPermissions.edit || customerPermissions.delete ? (
                      <TinyBtn onClick={() => openEditModal(p)}>
                        <FaEdit />
                        <span>{customerPermissions.edit ? "Edit" : "Manage"}</span>
                      </TinyBtn>
                      ) : "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {/* Edit Modal */}
      {isModalOpen && (
        <ModalOverlay
          onMouseDown={(e) =>
            e.target === e.currentTarget && setModalOpen(false)
          }
        >
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Party</ModalTitle>
              <CloseBtn onClick={() => setModalOpen(false)}>
                <FaTimes />
              </CloseBtn>
            </ModalHeader>

            <ModalBody>
              <Input
                disabled={!customerPermissions.edit}
                placeholder="Party Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
              <Input
                disabled={!customerPermissions.edit}
                placeholder="Company Name"
                value={editCompanyName}
                onChange={(e) => setEditCompanyName(e.target.value)}
              />
              <Input
                disabled={!customerPermissions.edit}
                placeholder="Mobile Number (10 digits)"
                value={editMobile}
                onChange={(e) =>
                  setEditMobile(
                    e.target.value.replace(/[^\d]/g, "").slice(0, 10)
                  )
                }
                inputMode="numeric"
              />
              <Select
                disabled={!customerPermissions.edit}
                value={editType}
                onChange={(e) => setEditType(e.target.value)}
              >
                <option value="">-- Select Type --</option>
                <option value="Customer">Customer</option>
                <option value="Supplier">Supplier</option>
              </Select>
            </ModalBody>

            <ModalActions>
              {customerPermissions.edit ? <PrimaryBtn onClick={handleUpdate}>
                <FaEdit />
                <span>Update</span>
              </PrimaryBtn> : null}
              {customerPermissions.delete ? <DangerBtn onClick={handleDelete}>
                <FaTrashAlt />
                <span>Delete</span>
              </DangerBtn> : null}
              <GrayBtn onClick={() => setModalOpen(false)}>Cancel</GrayBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Wrap>
  );
};

export default AddPartyPage;

/* ---------------- styled ---------------- */
const Wrap = styled.div`
  width: 100%;
  margin: 24px auto 48px;
  padding: 0 16px;
  background: ${color.bg};
  min-height: calc(100vh - 80px);
`;
const PermissionNotice = styled.div`
  margin-bottom: 14px;
  padding: 13px 15px;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 13px;
  font-weight: 700;
`;
const TopBar = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  border: 1px solid ${color.line};
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
  flex-wrap: wrap;
`;
const TopLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const TopTitle = styled.h1`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 900;
  color: ${color.text};
`;
const TopSub = styled.div`
  color: ${color.muted};
  font-size: 13px;
`;
const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const TopPill = styled.div`
  background: #f1f5f9;
  border: 1px solid ${color.line};
  color: ${color.text};
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 800;
  display: inline-flex;
  gap: 6px;
  font-size:12px
`;
const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${color.brand};
  border: 1px solid ${color.brand};
  color: #fff;
  padding: 10px 12px;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 10px 20px rgba(var(--app-primary-rgb), 0.18);
`;

const Card = styled.div`
  background: ${color.white};
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid ${color.line};
  box-shadow: 0 12px 36px rgba(var(--app-primary-rgb), 0.08);
`;
const CardTitle = styled.h2`
  margin: 2px 2px 12px;
  color: ${color.muted};
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;
const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  color: ${color.muted};
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.8fr 1.5fr 1.2fr;
  gap: 10px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  border: 1px solid ${color.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${color.text};
  font-size: 14px;
  outline: none;
  transition: all 0.12s ease;
  &:focus {
    border-color: ${color.brand};
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.12);
    background: #fff;
  }
`;
const Select = styled.select`
  height: 44px;
  border: 1px solid ${color.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${color.text};
  font-size: 14px;
  outline: none;
  transition: all 0.12s ease;
  &:focus {
    border-color: ${color.brand};
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.12);
    background: #fff;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  height: 40px;
  padding: 0 14px;
  background: ${color.brand};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.05s ease, box-shadow 0.12s ease;
  box-shadow: 0 10px 20px rgba(var(--app-primary-rgb), 0.18);
  &:hover {
    box-shadow: 0 12px 26px rgba(var(--app-primary-rgb), 0.26);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const HelpLine = styled.div`
  margin-top: 8px;
  color: ${color.muted};
  font-size: 12px;
`;
const HelpRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;
const FormActions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`;
const QuickTypeRow = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top:10px
`;
const QuickChip = styled.button`
  border: 1px solid ${color.line};
  background: ${({ $active }) => ($active ? color.brand : color.white)};
  color: ${({ $active }) => ($active ? "#fff" : color.text)};
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid ${color.line};
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  thead {
    background: ${color.brandSoft};
    position: sticky;
    top: 0;
    z-index: 1;
  }
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${color.line};
    text-align: left;
    font-size: 14px;
  }
  tbody tr:hover {
    background: #fafcff;
  }
`;
const Th = styled.th``;
const Td = styled.td``;

const TinyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${color.brand};
  background: ${color.brandSoft};
  color: ${color.brand};
  font-weight: 800;
  cursor: pointer;
`;
const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 800;
  background: ${({ $type }) =>
    $type === "Customer" ? "#ecf5ff" : $type === "Supplier" ? "#fef3c7" : "#eef2ff"};
  color: ${({ $type }) =>
    $type === "Customer" ? color.brand : $type === "Supplier" ? "#b45309" : color.text};
  border: 1px solid ${color.line};
`;
const PointsBadge = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid #b7e4dc;
  background: #e6f9f5;
  color: #087c70;
  font-size: 12px;
  font-weight: 900;
`;

const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${color.line};
  border-radius: 10px;
  background: #fff;
  color: ${color.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
    min-width: 200px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    width: 100%;
    input {
      min-width: 0;
      width: 100%;
    }
  }
`;

const Loading = styled.div`
  text-align: center;
  padding: 20px;
  color: ${color.muted};
`;
const Empty = styled.div`
  text-align: center;
  padding: 24px;
  color: ${color.muted};
`;

/* ----- Modal ----- */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  padding: 22px;
  z-index: 1000;
`;
const ModalCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;
const ModalTitle = styled.h4`
  margin: 0;
  font-size: 1.05rem;
  color: ${color.text};
  font-weight: 900;
`;
const CloseBtn = styled.button`
  background: transparent;
  border: 0;
  font-size: 1.1rem;
  cursor: pointer;
  color: #111;
`;
const ModalBody = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 10px;
`;
const ModalActions = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const DangerBtn = styled(PrimaryBtn)`
  background: ${color.danger};
`;
const GrayBtn = styled(PrimaryBtn)`
  background: ${color.grayBtn};
`;
